'use client';

import { useMemo, useCallback, type ReactElement } from 'react';
import WebHeader, { type HeaderItemProps } from '@pixum/web-header';
import type { FirstLevelItemProps } from '@pixum/web-navigation';
import { getCurrentUrl } from '@pixum/auth-core-ui';
import ThemeProvider from '@pixum/theme-provider';
import { trackEvent, TRACKING_ACTIONS, TRACKING_CATEGORIES } from 'utils';
import { PAGE_PATH, type WEB_HEADER_VARIANTS } from 'components/constants';
import { THEME_DEFAULT_PROPS } from 'data-layer/constants';
import { addClickEventToNavigationItems } from '../utils/transformNavigationNode';
import { addHeaderOnClickHandlers } from '../utils/addHeaderOnClickHandlers';

type WebHeaderClientProps = {
  centralItem: ReactElement;
  navigationData: FirstLevelItemProps[];
  headerData: HeaderItemProps[];
  variant: (typeof WEB_HEADER_VARIANTS)[keyof typeof WEB_HEADER_VARIANTS];
  withTopBar: boolean;
};

export function WebHeaderClient({
  centralItem,
  navigationData,
  headerData,
  variant,
  withTopBar
}: WebHeaderClientProps) {
  const onService = useCallback(
    () => trackEvent(TRACKING_CATEGORIES.HEADER, TRACKING_ACTIONS.SERVICE, PAGE_PATH.Service),
    []
  );

  const onLanguage = useCallback(
    () => trackEvent(TRACKING_CATEGORIES.HEADER, TRACKING_ACTIONS.LANGUAGE_SWITCHER, getCurrentUrl()),
    []
  );

  const onCart = useCallback(() => trackEvent(TRACKING_CATEGORIES.HEADER, TRACKING_ACTIONS.CART, getCurrentUrl()), []);

  const navigationItems = useMemo(() => addClickEventToNavigationItems(navigationData), [navigationData]);

  const headerItems = useMemo(
    () => addHeaderOnClickHandlers(headerData, { onService, onLanguage, onCart }),
    [headerData, onService, onLanguage, onCart]
  );

  return (
    <ThemeProvider theme={THEME_DEFAULT_PROPS} useWrapper={true}>
      <WebHeader
        centralItem={centralItem}
        headerItems={headerItems}
        navigationItems={navigationItems}
        withTopBar={withTopBar}
        variant={variant}
      />
    </ThemeProvider>
  );
}
